/* ==========================================================================
   YOPPY yoppy-cart.js — Shopify-backed cart module for the B2C storefront.
   --------------------------------------------------------------------------
   Port of the static site's cart.js (2026-07-14) onto the Shopify AJAX Cart
   API. Same DOM contract ([data-cart-*], [data-add-to-cart]="sku") and the
   same public window.YoppyCart API, so product-panel.js keeps working
   unchanged. Shopify is the cart/pricing authority: every mutation is a
   round-trip to /cart/*.js and the drawer re-renders from Shopify's JSON.
   PURCHASE MODEL unchanged: 1 case = 12 bags of ONE flavor. Quantities are
   cases. Free-shipping copy mirrors the storefront offer (6+ cases); actual
   rates are computed by Shopify checkout.
   ========================================================================== */
(function () {
  "use strict";

  var PIECES_PER_CASE = 12;
  var FREE_SHIP_MIN_CASES = 6;

  /* sku -> { id (variant), name, range, image, url, available } injected by
     snippets/yoppy-head.liquid from live product data. */
  var PRODUCTS = window.YOPPY_VARIANTS || {};

  var CartState = { cart: null, open: false, busy: false, scrollY: 0 };

  function euro(cents) { return "€" + (cents / 100).toFixed(2); }

  function skuForVariant(variantId) {
    for (var sku in PRODUCTS) {
      if (PRODUCTS[sku].id === variantId) return sku;
    }
    return null;
  }

  function totalCases() {
    return CartState.cart ? CartState.cart.item_count : 0;
  }

  /* ----- Shopify AJAX API -------------------------------------------------- */
  function api(path, opts) {
    return fetch(path, opts).then(function (res) {
      return res.json().then(function (data) {
        if (!res.ok) {
          var msg = (data && (data.description || data.message)) || "Cart error";
          var err = new Error(msg);
          err.status = res.status;
          throw err;
        }
        return data;
      });
    });
  }

  function refresh() {
    return api("/cart.js").then(function (cart) {
      CartState.cart = cart;
      renderCart();
      return cart;
    });
  }

  function addItem(sku, qtyCases) {
    var p = PRODUCTS[sku];
    if (!p || CartState.busy) return;
    CartState.busy = true;
    api("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ items: [{ id: p.id, quantity: Math.max(1, Math.floor(Number(qtyCases) || 1)) }] })
    })
      .then(function () { return refresh(); })
      .then(function () { bumpBadge(); })
      .catch(function (err) {
        openCart();
        setNote(err && err.status === 422
          ? "This flavor is temporarily out of stock. Email info@mermerfoods.com to reserve your cases."
          : "Something went wrong. Please try again or email info@mermerfoods.com.");
      })
      .finally(function () { CartState.busy = false; });
  }

  function updateQty(sku, qtyCases) {
    var p = PRODUCTS[sku];
    if (!p || CartState.busy) return;
    var qty = Math.max(0, Math.floor(Number(qtyCases) || 0));
    CartState.busy = true;
    api("/cart/change.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ id: String(p.id), quantity: qty })
    })
      .then(function (cart) { CartState.cart = cart; renderCart(); })
      .catch(function () { setNote("Could not update the cart. Please try again."); refresh(); })
      .finally(function () { CartState.busy = false; });
  }

  function removeItem(sku) { updateQty(sku, 0); }

  function clear() {
    api("/cart/clear.js", { method: "POST" }).then(function () { refresh(); });
  }

  function getTotal() {
    return CartState.cart ? euro(CartState.cart.total_price) : euro(0);
  }

  /* ----- Badge -------------------------------------------------------------- */
  function updateBadge() {
    var count = totalCases();
    document.querySelectorAll("[data-cart-count]").forEach(function (el) {
      el.textContent = String(count);
      el.classList.toggle("is-empty", count === 0);
    });
  }

  function bumpBadge() {
    document.querySelectorAll("[data-cart-count]").forEach(function (el) {
      el.classList.remove("bump");
      void el.offsetWidth;
      el.classList.add("bump");
    });
  }

  /* ----- Drawer rendering ---------------------------------------------------- */
  function lineMarkup(item) {
    var sku = skuForVariant(item.variant_id);
    var p = sku ? PRODUCTS[sku] : null;
    var name = p ? p.name : item.product_title.replace(/^YOPPY\s+/, "");
    var image = (p && p.image) || item.image || "";
    var key = sku || String(item.variant_id);
    var pieces = item.quantity * PIECES_PER_CASE;
    return (
      '<div class="cart-line" data-cart-line="' + key + '">' +
        '<div class="cart-line-thumb"><img src="' + image + '" alt="" loading="lazy"></div>' +
        '<div class="cart-line-main">' +
          '<strong class="cart-line-name">' + name + '</strong>' +
          '<span class="cart-line-meta">' + item.quantity + ' case(s) &middot; ' + pieces + ' pcs</span>' +
          '<div class="qty-stepper" role="group" aria-label="Cases of ' + name + '">' +
            '<button type="button" class="qty-step" data-cart-dec="' + key + '" aria-label="Remove one case">−</button>' +
            '<input class="qty-input" type="number" inputmode="numeric" min="1" value="' + item.quantity + '" data-cart-qty="' + key + '" aria-label="Number of cases">' +
            '<button type="button" class="qty-step" data-cart-inc="' + key + '" aria-label="Add one case">+</button>' +
          '</div>' +
        '</div>' +
        '<div class="cart-line-side">' +
          '<span class="cart-line-total">' + euro(item.final_line_price) + '</span>' +
          '<button type="button" class="cart-line-remove" data-cart-remove="' + key + '">Remove</button>' +
        '</div>' +
      '</div>'
    );
  }

  function summaryMarkup() {
    var cart = CartState.cart;
    var cases = cart.item_count;
    var hint = "";
    if (cases > 0 && cases < FREE_SHIP_MIN_CASES) {
      hint = '<p class="cart-hint">Add ' + (FREE_SHIP_MIN_CASES - cases) + ' more case(s) to unlock free shipping.</p>';
    } else if (cases >= FREE_SHIP_MIN_CASES) {
      hint = '<p class="cart-hint free">✓ Free shipping unlocked!</p>';
    }
    return (
      '<div class="cart-row"><span>Subtotal</span><span>' + euro(cart.items_subtotal_price) + '</span></div>' +
      '<div class="cart-row cart-total"><span>Total</span><span>' + euro(cart.total_price) + '</span></div>' +
      hint +
      '<button type="button" class="btn btn-primary btn-block" data-cart-checkout>Secure Checkout →</button>' +
      '<p class="cart-secure">Secure card payment. Shipping calculated at checkout — free across Europe on 6+ cases.</p>' +
      '<p class="cart-note" data-cart-note role="status"></p>'
    );
  }

  function renderCart() {
    updateBadge();
    var body = document.querySelector("[data-cart-body]");
    var foot = document.querySelector("[data-cart-foot]");
    if (!body || !foot) return;
    if (!CartState.cart || CartState.cart.items.length === 0) {
      body.innerHTML = '<p class="cart-empty">Your cart is empty.<br>Pick a flavor to get crunching.</p>';
      foot.innerHTML = "";
    } else {
      body.innerHTML = CartState.cart.items.map(lineMarkup).join("");
      foot.innerHTML = summaryMarkup();
    }
    // Re-translate the freshly rendered cart into the active language (M-05).
    if (window.mermerI18n && typeof window.mermerI18n.apply === "function") {
      var drawer = document.querySelector("[data-cart-drawer]");
      if (drawer) window.mermerI18n.apply(drawer);
    }
  }

  function setNote(text) {
    var note = document.querySelector("[data-cart-note]");
    if (note) { note.textContent = text; note.style.color = "#c0151b"; }
  }

  /* ----- Drawer open / close ------------------------------------------------- */
  function lockScroll() {
    CartState.scrollY = window.scrollY || window.pageYOffset || 0;
    document.body.style.top = "-" + CartState.scrollY + "px";
    document.body.classList.add("cart-open");
  }

  function unlockScroll() {
    document.body.classList.remove("cart-open");
    document.body.style.top = "";
    window.scrollTo(0, CartState.scrollY || 0);
  }

  function openCart() {
    var drawer = document.querySelector("[data-cart-drawer]");
    if (!drawer) return;
    CartState.open = true;
    drawer.setAttribute("aria-hidden", "false");
    lockScroll();
    var closeBtn = drawer.querySelector("[data-cart-close]");
    if (closeBtn) closeBtn.focus({ preventScroll: true });
  }

  function closeCart() {
    var drawer = document.querySelector("[data-cart-drawer]");
    if (!drawer) return;
    CartState.open = false;
    drawer.setAttribute("aria-hidden", "true");
    unlockScroll();
    var toggle = document.querySelector("[data-cart-toggle]");
    if (toggle) toggle.focus({ preventScroll: true });
  }

  /* ----- Checkout ------------------------------------------------------------ */
  function checkout() {
    if (!CartState.cart || CartState.cart.items.length === 0) return;
    var button = document.querySelector("[data-cart-checkout]");
    if (button) { button.disabled = true; button.textContent = "Redirecting to secure checkout…"; }
    window.location.href = "/checkout";
  }

  /* ----- "Add to cart" button feedback ---------------------------------------- */
  function flashAdded(btn) {
    if (!btn) return;
    btn.classList.add("is-added");
    window.setTimeout(function () { btn.classList.remove("is-added"); }, 1400);
  }

  /* ----- Event wiring ---------------------------------------------------------- */
  function wire() {
    document.addEventListener("click", function (event) {
      var el = event.target;
      if (!el.closest) return;

      var add = el.closest("[data-add-to-cart]");
      if (add && !add.disabled) {
        event.preventDefault();
        addItem(add.getAttribute("data-add-to-cart"), 1);
        flashAdded(add);
        return;
      }
      if (el.closest("[data-cart-toggle]")) { event.preventDefault(); openCart(); return; }
      if (el.closest("[data-cart-close], [data-cart-overlay]")) { closeCart(); return; }

      var inc = el.closest("[data-cart-inc]");
      if (inc) {
        var skuInc = inc.getAttribute("data-cart-inc");
        var itemInc = currentItem(skuInc);
        if (itemInc) updateQty(skuInc, itemInc.quantity + 1);
        return;
      }
      var dec = el.closest("[data-cart-dec]");
      if (dec) {
        var skuDec = dec.getAttribute("data-cart-dec");
        var itemDec = currentItem(skuDec);
        if (itemDec) updateQty(skuDec, itemDec.quantity - 1);
        return;
      }
      var rm = el.closest("[data-cart-remove]");
      if (rm) { removeItem(rm.getAttribute("data-cart-remove")); return; }
      if (el.closest("[data-cart-checkout]")) { checkout(); return; }
    });

    document.addEventListener("change", function (event) {
      var input = event.target.closest ? event.target.closest("[data-cart-qty]") : null;
      if (input) updateQty(input.getAttribute("data-cart-qty"), input.value);
    });

    document.addEventListener("keydown", function (event) {
      if (event.key === "Escape" && CartState.open) closeCart();
    });
  }

  function currentItem(sku) {
    var p = PRODUCTS[sku];
    if (!p || !CartState.cart) return null;
    for (var i = 0; i < CartState.cart.items.length; i++) {
      if (CartState.cart.items[i].variant_id === p.id) return CartState.cart.items[i];
    }
    return null;
  }

  /* ----- Public API (contract kept for product-panel.js) ----------------------- */
  window.YoppyCart = {
    state: CartState,
    products: PRODUCTS,
    addItem: addItem,
    removeItem: removeItem,
    updateQty: updateQty,
    clear: clear,
    getTotal: getTotal,
    renderCart: renderCart,
    openCart: openCart,
    closeCart: closeCart,
    count: totalCases
  };

  /* ----- Init -------------------------------------------------------------------- */
  function init() {
    wire();
    refresh();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
